import React, { useEffect } from "react";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet"; 
import FAQ from "../components/FAQSection";

const garageSoftwareFAQs = [
    {
        q: "What is Garage Management Software?",
        a: "Garage Management Software is a digital platform that helps streamline garage operations like bookings, invoicing, inventory, and customer communication—all in one place."
    },
    {
        q: "Do I need technical expertise to use the software?",
        a: "No. We provide full onboarding, setup, and training. The systems we recommend are user-friendly and tailored for non-technical garage owners."
    },
    {
        q: "Can it integrate with my existing tools?",
        a: "Yes. We work with platforms that integrate smoothly with CRM, marketing tools, and accounting software to keep everything connected."
    },
    {
        q: "Is it cloud-based?",
        a: "Yes. You can access your garage dashboard from any device, anywhere. Secure cloud-based solutions ensure safety and flexibility."
    },
    {
        q: "What kind of garages is this suitable for?",
        a: "Our solutions are ideal for independent garages, MOT stations, service centers, and multi-location operations."
    }
];

const GarageSoftwareSetup = () => {
    useEffect(() => {
        document.title = "Garage Management Software Setup | MM Auto Tech";
    }, []);

    return (
        <><Helmet>
  <title>Garage Software Setup | MM AutoTech</title>
  <meta
    name="description"
    content="We provide tailored garage management software setup to streamline your operations and boost efficiency."
  />
  <link rel="canonical" href="https://mmautotech.com/services/garage-software-setup" />
</Helmet>
<div className="max-w-7xl mx-auto space-y-24">
                {/* Hero Section */}
                <section className="flex flex-col lg:flex-row items-stretch bg-blue-900 text-white">
                    {/* Left Image (Foreground) */}
                    <div
                        className="lg:w-1/2 h-64 lg:h-auto relative overflow-hidden"
                        style={{ clipPath: "polygon(0 0, 85% 0, 100% 100%, 0% 100%)" }} // inverse clip for left
                    >
                        <img
                            src="https://www.autointouch.online/wp-content/uploads/2021/05/header-image-only-new-900px-wide-aitol.png"
                            alt="Garage Software Setup"
                            className="w-full h-72 md:h-full object-cover" />
                    </div>

                    {/* Right Content */}
                    <div className="lg:w-1/2 p-8 flex flex-col justify-center">
                        <h1 className="text-yellow-400 text-sm font-semibold uppercase mb-4">Garage Software Setup</h1>
                        <h2 className="text-4xl font-bold mb-6 leading-snug">Digital Transformation for Your Garage Starts Here</h2>
                        <p className="text-lg text-gray-200 mb-6">
                            We help garages like yours transition from paper chaos to smart, streamlined software solutions—saving time, reducing errors, and boosting profits.
                        </p>
                        <Link
                            to="/book-appointment"
                            className="inline-flex items-center justify-center text-sm font-semibold tracking-widest text-white border border-white px-6 py-2 rounded hover:bg-yellow-400 hover:border-yellow-400 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-900/30"
                            aria-label="Book an appointment"
                        >
                            Book a Free Setup Consultation
                        </Link>
                    </div>
                </section>

                <section className="py-16 px-6 bg-white text-gray-900">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-3xl font-bold text-center text-blue-900 mb-4">
                            All-in-One Garage Software Setup
                        </h2>
                        <p className="text-center text-gray-600 text-lg mb-12">
                            We configure and deploy the tools your garage needs to run smarter—from bookings and billing to customer care and inventory management. No tech headaches, just seamless automation.
                        </p>

                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="border border-gray-200 p-6 rounded-lg shadow hover:shadow-md transition">
                                <h3 className="font-semibold text-lg text-blue-800 mb-2">Online Booking System</h3>
                                <p className="text-sm text-gray-700">Let customers book appointments 24/7 via your website or phone—automated confirmations and reminders included.</p>
                            </div>
                            <div className="border border-gray-200 p-6 rounded-lg shadow hover:shadow-md transition">
                                <h3 className="font-semibold text-lg text-blue-800 mb-2">Digital Job Cards & Invoicing</h3>
                                <p className="text-sm text-gray-700">Create and manage jobs, issue estimates, and send professional invoices with just a few clicks.</p>
                            </div>
                            <div className="border border-gray-200 p-6 rounded-lg shadow hover:shadow-md transition">
                                <h3 className="font-semibold text-lg text-blue-800 mb-2">Real-Time Inventory Control</h3>
                                <p className="text-sm text-gray-700">Track stock levels, manage suppliers, and prevent over-ordering with smart inventory tools.</p>
                            </div>
                            <div className="border border-gray-200 p-6 rounded-lg shadow hover:shadow-md transition">
                                <h3 className="font-semibold text-lg text-blue-800 mb-2">Integrated CRM & Reminders</h3>
                                <p className="text-sm text-gray-700">Automate service reminders, follow-ups, and feedback requests to boost repeat business.</p>
                            </div>
                            <div className="border border-gray-200 p-6 rounded-lg shadow hover:shadow-md transition">
                                <h3 className="font-semibold text-lg text-blue-800 mb-2">Multi-Location Dashboard</h3>
                                <p className="text-sm text-gray-700">View and control multiple garage locations from one centralized interface—ideal for growing businesses.</p>
                            </div>
                            <div className="border border-gray-200 p-6 rounded-lg shadow hover:shadow-md transition">
                                <h3 className="font-semibold text-lg text-blue-800 mb-2">Team Training & Onboarding</h3>
                                <p className="text-sm text-gray-700">We train your entire team and provide tailored onboarding so everyone’s ready from day one.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ & CTA */}
                <FAQ data={garageSoftwareFAQs} />
                {/* CTA: Business Support Focused Conversion Block */}
                <section className="py-16 bg-[#0f172a] text-white px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                            Streamline Your Garage, One Task at a Time
                        </h2>
                        <p className="text-lg mb-6">
                            From admin overload to missed bookings, we help you fix the bottlenecks holding your business back. Let’s create a system that works for you—not against you.
                        </p>
                        <Link to="/bookappointment">
                            <button className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-semibold px-6 py-3 rounded shadow transition-all duration-300">
                                Book Your Free Business Support Call
                            </button>
                        </Link>
                    </div>
                </section>
            </div></>
    );
};

export default GarageSoftwareSetup;
